import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/utils";
import { ALargeSmall, ChevronDown } from "lucide-react";
import { useState } from "react";
import { canvasToolbarButtonVariants } from "./canvasToolbarStyles";

export interface CanvasTypeface {
  name: string;
  value: string;
}

/**
 * The typefaces text labels, shapes and line labels offer — the schema's
 * font_family enum, plus Modern, which the client normalises to Inter.
 */
export const CANVAS_TYPEFACES: CanvasTypeface[] = [
  { name: "Simple", value: "Inter, sans-serif" },
  { name: "Bookish", value: "Georgia, serif" },
  { name: "Technical", value: "'Courier New', monospace" },
  { name: "Scribbled", value: "'Comic Sans MS', cursive" },
  { name: "Modern", value: "'Inter', system-ui, sans-serif" },
];

interface CanvasToolbarTypefaceMenuProps {
  fontFamily?: string;
  /** Override the list (notes keep their own values). */
  fonts?: CanvasTypeface[];
  onFontFamilyChange: (fontFamily: string) => void;
  onOpenChange?: (open: boolean) => void;
}

/** Type-2 dropdown: one row per typeface, set in its own face. */
export function CanvasToolbarTypefaceMenu({
  fontFamily,
  fonts = CANVAS_TYPEFACES,
  onFontFamilyChange,
  onOpenChange,
}: CanvasToolbarTypefaceMenuProps) {
  const [open, setOpen] = useState(false);

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    onOpenChange?.(next);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <button
              className={cn(canvasToolbarButtonVariants({ hasDropdown: true }))}
              onClick={(e) => e.stopPropagation()}
            >
              <ALargeSmall className="size-4" />
              <ChevronDown className="size-2.5" strokeWidth={2.5} />
            </button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="top">Typeface</TooltipContent>
      </Tooltip>
      <PopoverContent
        className="w-40 p-1"
        align="center"
        side="top"
        sideOffset={8}
        onClick={(e) => e.stopPropagation()}
      >
        {fonts.map((font) => (
          <button
            key={font.value}
            onClick={() => {
              onFontFamilyChange(font.value);
              handleOpenChange(false);
            }}
            className={cn(
              "w-full px-3 py-2 text-left text-sm rounded-lg transition-colors",
              "hover:bg-n-95/8 dark:hover:bg-n-95/10",
              fontFamily === font.value &&
                "bg-primary/10 text-primary font-medium",
            )}
            style={{ fontFamily: font.value }}
          >
            {font.name}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}
